import {
  Button,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import React, { Fragment, useContext } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SimpleAlert from "../../../components/display/SimpleAlert";
import { GlobalDataContext } from "../../../contexts/GlobalData";
import { SnackbarContext } from "../../../contexts/Snackbar";
import { CHANGE_EMPLOYEE_PERMISSION_IN_CLINIC } from "../../../enums/PermissionTitles";
import { PERMISSIONS_EDIT } from "../../../enums/QueryKeys";
import { snackbarSeverity } from "../../../hooks/useSnackbar";
import useUser from "../../../hooks/useUser";
import { capitalizeFirstLetter } from "../../../utils/Functions";
import usePermissionPut from "./hooks/usePermissionPut";
import * as Functions from "./utils/PermissionTabFunctions";
import { useGetClinicPermissionsForEmployee } from "../../../hooks/useGetClinicPermissionsForEmployee";
import { useGetUserRole } from "../../../hooks/useGetUserRole";
import { ADMIN } from "../../../enums/Roles";
const permissionsOrder = [
  "VIEW",
  "CREATE",
  "ADD",
  "EDIT",
  "DELETE",
  "REJECT",
  "ACCEPT",
  "CHANGE_OWNER",
  "CHANGE_EMPLOYEE",
];

/** Admin permission switches always show fully granted and locked, since PermissionServiceImpl#doesUserHavePermission computes the tier's full set live - prevents an admin from accidentally revoking their own access. */
export default function UserPermissionsPage({ user, permissions }) {
  const { id: userId } = useParams();
  const {roles: loggedInUserRoles} = useUser();
  const {data:pageUserRole}=useGetUserRole(userId);
  const { permissionsMap, isPermissionAllowed } = useContext(GlobalDataContext);
  const hasPermission = isPermissionAllowed(
    CHANGE_EMPLOYEE_PERMISSION_IN_CLINIC
  );
  const { t } = useTranslation();
  const { handleSubmit, watch, setValue } = useForm();
  const [usersPermissions, setUsersPermissions] = React.useState([]);
  const { data: clinicAllPermissions, isLoading, error } = useGetClinicPermissionsForEmployee(userId);

  const put = usePermissionPut(
    "/user/edit/" + userId,
    PERMISSIONS_EDIT + userId
  );

  const { handleSnackbar } = React.useContext(SnackbarContext);
  const isEmployeeEditingAdmin= loggedInUserRoles.employee && pageUserRole===ADMIN;
  const isTargetAdmin = pageUserRole === ADMIN;

  React.useEffect(() => {
    setValue("user", user);
    if (user?.userPermissions != null) {
      setUsersPermissions(user?.userPermissions);
    }
    else{
      setUsersPermissions(permissions);
    }

  }, [permissions, setValue, user, user?.userPermissions]);

  function handleSwitchChange(givenId, title) {
    //Find permission by title
    const permission = title;
    const isAppended = Object.values(usersPermissions).find(
      (perm) => String(perm) === String(permission)
    );

    //if view is present remove all assosiated permissions
    if (String(title).includes("VIEW") && isAppended) {
      const keyword = String(permission.title).split("_")[1];
      setUsersPermissions((oldData) =>
        oldData.filter((perm) => !String(perm).includes(String(title)))
      );
    }

    //Remove if found
    if (isAppended) {
      setUsersPermissions((oldData) =>
        oldData.filter((perm) => String(perm) !== String(title))
      );
    } //Append if not found
    else {
      setUsersPermissions((oldData) => [...oldData, permission]);
    }
  }

  function categorizePermissions(allPermissions) {
    if (!isLoading) {
      const permissions = permissionsMap(allPermissions);
      return Object.entries(permissions).map(([key, array]) => {
        return <Row key={key} title={key} array={array} />;
      });
    }
  }

  function Row({ title, array }) {
    return (
      <Stack m={2} mb={4}>
        <Typography variant="caption">
          {t(capitalizeFirstLetter(title))}
        </Typography>
        <Divider />
        <Stack
          direction={"row"}
          sx={{
            flexWrap: "wrap",
          }}
        >
          {array
            .sort((a, b) => {
              const indexA = permissionsOrder.findIndex((permission) =>
                a.action.includes(permission)
              );
              const indexB = permissionsOrder.findIndex((permission) =>
                b.action.includes(permission)
              );

              if (indexA < indexB) return -1;
              if (indexA > indexB) return 1;
              return 0;
            })
            .map((item, index) => {
              return (
                <Fragment key={index}>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    width={150}
                    spacing={1}
                    ml={2}
                    mr={2}
                  >
                    <FormControlLabel
                      label={t(capitalizeFirstLetter(item.action))}
                      labelPlacement="start"
                      onChange={() => handleSwitchChange(item.id, item.title)}
                      control={
                        <Switch
                          checked={isTargetAdmin || isCheckedValue(item.title, usersPermissions)}
                          disabled={isTargetAdmin || !hasPermission}
                        />
                      }
                    />
                  </Stack>
                  {showDivider(index, array.length)}
                </Fragment>
              );
            })}
        </Stack>
        <Divider />
      </Stack>
    );
  }

  function showDivider(index, length) {
    if (index < length - 1) {
      return <Divider orientation="vertical" variant="middle" flexItem />;
    }
  }

  function isCheckedValue(title, userPermissions) {
    const permissionIds = [];
    userPermissions.forEach((perm) => permissionIds.push(perm));

    if (permissions != null) {
      return permissionIds?.includes(title);
    }

    return false;
  }

  async function submitData() {
    if (hasPermission) {
      const user = watch("user");
      const new_obj = {
        ...user,
        userPermissions: usersPermissions,
      };
      put.submitCallback(new_obj);
    } else {
      handleSnackbar(t("NoPermissionForThisAction"), snackbarSeverity.ERROR);
    }
  }

  if (user?.userPermissions == null) {
    return (
      <SimpleAlert message={"NoPermissionsFoundForUser"} severity={"error"} />
    );
  }

  return (
    <Fragment>
      <form onSubmit={handleSubmit(submitData)}>
        {categorizePermissions(clinicAllPermissions)}
        <Stack spacing={2} direction={"row"} justifyContent={"space-between"}>
          <Stack spacing={2} direction={"row"}>
            <Button
              type={"submit"}
              disabled={isEmployeeEditingAdmin || isTargetAdmin || Functions.isButtonDisabled(
                user,
                usersPermissions,
                user?.userPermissions,
                hasPermission
              )}
              variant="contained"
            >
              {t("Save")}
            </Button>
            <Button
              onClick={() =>
                Functions.resetUserPermissions(
                  setUsersPermissions,
                  user?.userPermissions
                )
              }
              color="error"
              variant="contained"
              disabled={isTargetAdmin || Functions.isButtonDisabled(
                user,
                usersPermissions,
                user?.userPermissions,
                hasPermission
              )}
            >
              {t("Reset")}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Fragment>
  );
}
